/**
 * CapacityVisual — OutpostForge frontend
 * Renders a slotting-core DropletLayout: instance blocks proportional to
 * memory, NUMA nodes with boundary separators, free space, and the flat
 * over-capacity fallback with exceeded highlighting. Slots occupied by
 * running instances get a darker bottom band (usedByType). Tooltips are
 * custom (instant on hover — native title has a fixed browser delay).
 * Compact mode renders a thin strip for the rack panoramic.
 */

import { useState, type MouseEvent } from 'react';
import type { DropletLayout, LayoutBlock } from 'slotting-core';
import '../visualizer.css';

interface TooltipState {
  text: string;
  x: number;
  y: number;
}

type HoverHandler = (text: string, event: MouseEvent) => void;

function Block({
  block,
  used = false,
  onHover,
  onLeave,
}: {
  block: LayoutBlock;
  used?: boolean;
  onHover: HoverHandler;
  onLeave: () => void;
}) {
  if (block.kind === 'free') {
    return (
      <div
        className="instance-block free-space"
        style={{ width: `${block.widthPct}%` }}
        data-tooltip={block.tooltip}
        onMouseEnter={(e) => onHover(block.tooltip, e)}
        onMouseMove={(e) => onHover(block.tooltip, e)}
        onMouseLeave={onLeave}
      />
    );
  }
  const classes = ['instance-block'];
  if (block.spanning) classes.push('spanning-instance');
  if (block.exceeded) classes.push('exceeded');
  if (used) classes.push('in-use');
  const tooltip = used ? `${block.tooltip} · in use` : block.tooltip;
  return (
    <div
      className={classes.join(' ')}
      style={{ width: `${block.widthPct}%`, backgroundColor: block.color }}
      data-tooltip={tooltip}
      onMouseEnter={(e) => onHover(tooltip, e)}
      onMouseMove={(e) => onHover(tooltip, e)}
      onMouseLeave={onLeave}
    />
  );
}

export function CapacityVisual({
  layout,
  compact = false,
  usedByType,
}: {
  layout: DropletLayout;
  compact?: boolean;
  /**
   * Running instances per type. The API does not expose which physical slot
   * an instance occupies, so the first N slots of each type get the in-use
   * indicator — the count is what matters.
   */
  usedByType?: Record<string, number>;
}) {
  const className = compact ? 'capacity-visual capacity-visual--compact' : 'capacity-visual';
  const [tip, setTip] = useState<TooltipState | null>(null);

  const onHover: HoverHandler = (text, event) => {
    setTip({ text, x: event.clientX, y: event.clientY });
  };
  const onLeave = () => setTip(null);

  // Per-render counter: blocks render in a deterministic order, so marking
  // "the first N of each type" is stable across renders
  const counter: Record<string, number> = {};
  const isUsed = (block: LayoutBlock): boolean => {
    if (block.kind !== 'instance' || !usedByType) return false;
    const seen = (counter[block.typeName] ?? 0) + 1;
    counter[block.typeName] = seen;
    return seen <= (usedByType[block.typeName] ?? 0);
  };

  const tooltipElement = tip && (
    <div className="capacity-tooltip" role="tooltip" style={{ left: tip.x, top: tip.y }}>
      {tip.text}
    </div>
  );

  if (layout.mode === 'numa') {
    return (
      <div className={className} data-testid="capacity-visual">
        {layout.spanningBlocks.map((block, i) => (
          <Block
            key={`span-${i}`}
            block={block}
            used={isUsed(block)}
            onHover={onHover}
            onLeave={onLeave}
          />
        ))}
        {layout.numaNodes.map((node, n) => (
          // NUMA nodes interleaved with boundary separators
          <div key={`node-wrap-${n}`} className="numa-node-wrap" style={{ display: 'contents' }}>
            <div className="numa-node" style={{ width: `${node.widthPct}%` }}>
              {node.blocks.map((block, i) => (
                <Block
                  key={i}
                  block={block}
                  used={isUsed(block)}
                  onHover={onHover}
                  onLeave={onLeave}
                />
              ))}
            </div>
            {n < layout.numaNodes.length - 1 && (
              <div
                className="numa-separator"
                data-tooltip={layout.separatorTitle}
                onMouseEnter={(e) => onHover(layout.separatorTitle, e)}
                onMouseMove={(e) => onHover(layout.separatorTitle, e)}
                onMouseLeave={onLeave}
              />
            )}
          </div>
        ))}
        {tooltipElement}
      </div>
    );
  }

  return (
    <div className={className} data-testid="capacity-visual">
      {layout.flatBlocks.map((block, i) => (
        <Block key={i} block={block} used={isUsed(block)} onHover={onHover} onLeave={onLeave} />
      ))}
      {tooltipElement}
    </div>
  );
}
